/* =====================================================================
   tower/driver/src/look.js — which theme is on the screen right now (driver handheld A4/A5).

   display.js says what the driver CHOSE (layout, a day theme, a night theme, and Auto · Day · Night). This
   file decides what APPLIES at this moment and paints it:

     · Auto follows the sun at the phone's own location — sunrise to sunset is "day", the rest is "night".
       Computed on the phone (vendor-suncalc.js), so it works with no signal at all. The place is the last
       GPS fix this phone had (kept, rounded to ~1 km, in its own storage), else the route's hub, else the
       company's first warehouse; with none of those, the phone's clock (7 AM–7 PM) — and it says so.
     · The swap happens only when the screen is idle: never while a signature is being drawn, a payment is
       being taken, a field is being typed in, or within a few seconds of a tap. It waits and tries again.
     · The driver's own choice (Pick your look, settings) applies at once — it is what they just asked for.

   Painting = the theme's tokens as --dh-* custom properties on <html> (themes.js), plus
   data-drv-theme / data-drv-tone for the stylesheet. Layout is stamped by display.js (data-drv-layout).
   ===================================================================== */
import { display, onDisplay } from './display.js';
import { paintTokens, toneOf } from './themes.js';
import { getPosition, getTimes, HORIZON_DEG } from './vendor-suncalc.js';

const FIX_KEY = 'routingapp:driver:lastfix:v1';
const PICKED_KEY = 'routingapp:driver:look-picked:v1';
const IDLE_MS = 8000;                 // a tap this recent means the driver is mid-action
let _applied = null, _pending = null, _lastInput = 0, _lastWhy = '';
const _subs = new Set();

/* ── where the phone is ─────────────────────────────────────────────────────────────────────── */
/** Remember a fix (from the route's GPS stream or a one-off read), rounded: the sun does not need metres. */
export function noteFix(lat, lng) {
  if (!isFinite(lat) || !isFinite(lng)) return;
  try { localStorage.setItem(FIX_KEY, JSON.stringify({ lat: Math.round(lat * 100) / 100, lng: Math.round(lng * 100) / 100, at: Date.now() })); } catch (e) {}
}
function _hubPlace() {
  try {
    const r = typeof window._drvR === 'function' ? window._drvR() : null;
    if (r && typeof hubLL === 'function') { const ll = hubLL(r.hub); if (ll && isFinite(ll[0])) return { lat: ll[0], lng: ll[1], source: 'hub' }; }
  } catch (e) {}
  try {
    const h = (typeof HUBS !== 'undefined' && HUBS) ? (Array.isArray(HUBS) ? HUBS[0] : Object.values(HUBS)[0]) : null;
    const ll = h && (h.ll || (h.lat != null ? [h.lat, h.lng] : null));
    if (ll && isFinite(ll[0])) return { lat: ll[0], lng: ll[1], source: 'company' };
  } catch (e) {}
  return null;
}
/** The place the sun is computed for, and where that place came from. */
export function place() {
  try { const f = JSON.parse(localStorage.getItem(FIX_KEY) || 'null'); if (f && isFinite(f.lat)) return { lat: f.lat, lng: f.lng, source: 'gps', at: f.at }; } catch (e) {}
  return _hubPlace() || { lat: null, lng: null, source: 'clock' };
}

/* ── day or night ───────────────────────────────────────────────────────────────────────────── */
/** Is it daytime here, now? Sun above the horizon (with refraction) — or 7 AM–7 PM on the phone's clock. */
export function sun(now) {
  const at = now instanceof Date ? now : new Date(now || Date.now());
  const p = place();
  if (p.lat == null) { const h = at.getHours(); return { isDay: h >= 7 && h < 19, source: 'clock', sunrise: null, sunset: null }; }
  const alt = getPosition(at, p.lat, p.lng).altitude * 180 / Math.PI;
  const tm = getTimes(at, p.lat, p.lng);
  const ok = (d) => d instanceof Date && !isNaN(d) ? d : null;
  return { isDay: alt > HORIZON_DEG, source: p.source, sunrise: ok(tm.sunrise), sunset: ok(tm.sunset), lat: p.lat, lng: p.lng };
}

/** The theme the driver's choice calls for right now (before the idle gate). */
export function wanted(now) {
  const e = display().effective;
  if (e.themeMode === 'day') return { theme: e.dayTheme, why: 'day' };
  if (e.themeMode === 'night') return { theme: e.nightTheme, why: 'night' };
  const s = sun(now);
  return { theme: s.isDay ? e.dayTheme : e.nightTheme, why: s.isDay ? 'sun-day' : 'sun-night' };
}

/* ── the idle gate ──────────────────────────────────────────────────────────────────────────── */
/** Why a theme swap must wait, or '' when the screen is idle. Never mid-signature, mid-payment or mid-typing. */
export function busyReason() {
  try {
    if (document.getElementById('drvSignSheet')) return 'signature';
    if (document.getElementById('dhTenders')) return 'payment';   // the Collect screen is open
    const a = document.activeElement;
    if (a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) && a.type !== 'button') return 'typing';
    if (Date.now() - _lastInput < IDLE_MS) return 'recent-tap';
  } catch (e) {}
  return '';
}

function _paint(id) {
  const h = document.documentElement;
  paintTokens(h, id);
  h.dataset.drvTheme = id; h.dataset.drvTone = toneOf(id);
  // the browser's own chrome (status bar on Android) follows the theme
  try { let m = document.querySelector('meta[name="theme-color"]'); if (!m) { m = document.createElement('meta'); m.name = 'theme-color'; document.head.appendChild(m); }
    m.content = getComputedStyle(h).getPropertyValue('--dh-surf').trim() || m.content; } catch (e) {}
  _applied = id; _pending = null;
  _subs.forEach((fn) => { try { fn(state()); } catch (e) {} });
}

/**
 * Bring the screen to the theme that should apply. `force` = the driver's own change (instant); otherwise a
 * swap waits for an idle screen and is retried by the tick.
 */
export function applyLook(force, now) {
  const w = wanted(now); _lastWhy = w.why;
  if (w.theme === _applied) { _pending = null; return false; }
  if (!force && _applied) { const b = busyReason(); if (b) { _pending = w.theme; return false; } }
  _paint(w.theme);
  return true;
}

export function state() {
  const e = display().effective, s = sun();
  return { mode: e.themeMode, dayTheme: e.dayTheme, nightTheme: e.nightTheme, layout: e.layout,
    applied: _applied, pending: _pending, why: _lastWhy, isDay: s.isDay, source: s.source,
    sunrise: s.sunrise ? s.sunrise.getTime() : null, sunset: s.sunset ? s.sunset.getTime() : null, busy: busyReason() };
}
export function onLook(fn) { _subs.add(fn); return () => _subs.delete(fn); }

/* ── Pick your look (first launch) ──────────────────────────────────────────────────────────── */
/** Has this driver chosen a look — on this phone, or on their record from another phone? */
export function lookPicked() {
  try { if (localStorage.getItem(PICKED_KEY)) return true; } catch (e) {}
  const p = display().pref;
  return p.layout != null || p.dayTheme != null || p.nightTheme != null || p.themeMode != null;
}
export function markLookPicked() { try { localStorage.setItem(PICKED_KEY, String(Date.now())); } catch (e) {} }

/* ── wiring ─────────────────────────────────────────────────────────────────────────────────── */
// Input of any kind marks the screen as in use (capture phase: a handler that stops propagation still counts).
['pointerdown', 'keydown', 'input'].forEach((ev) => { try { window.addEventListener(ev, () => { _lastInput = Date.now(); }, true); } catch (e) {} });
// The driver's (or the office's) choice: at once.
onDisplay(() => applyLook(true));
// The sun: every 30 seconds, through the idle gate.
try { setInterval(() => { if (!document.hidden) applyLook(false); }, 30000); } catch (e) {}
try { document.addEventListener('visibilitychange', () => { if (!document.hidden) applyLook(false); }); } catch (e) {}
// A fresh fix when the phone already allows location — never a permission prompt just for the colours.
try {
  if (navigator.permissions && navigator.geolocation) navigator.permissions.query({ name: 'geolocation' }).then((p) => {
    if (p && p.state === 'granted') navigator.geolocation.getCurrentPosition((pos) => { noteFix(pos.coords.latitude, pos.coords.longitude); applyLook(false); },
      () => {}, { enableHighAccuracy: false, maximumAge: 3600000, timeout: 15000 });
  }).catch(() => {});
} catch (e) {}
applyLook(true);

Object.assign(window, { drvLook: state, drvLookApply: applyLook, drvLookBusy: busyReason, drvLookPicked: lookPicked, drvNoteFix: noteFix, drvSun: sun });
